import { useCallback, useState } from 'react';
import { ConfirmationModal } from '@library/components/modals/ConfirmationModal';
import { Loading } from '@library/components/Loading';
import type { MemberDetailsContext } from '@bo-hooks/members/useMemberDetailsContext';
import { MemberNotesTable } from '@bo-components/members/memberNotes';
import { useMemberNotes } from '@bo-hooks/members';
import { useOutletContext } from 'react-router-dom';

export const MemberNotes = () => {
  const [selectedNoteId, setSelectedNoteId] = useState<string | undefined>(
    undefined
  );

  const { isResetCurrentPage, onResetCurrentPage } =
    useOutletContext<MemberDetailsContext>();

  const {
    currentPage,
    notes,
    onCurrentPageChange,
    onDeleteMemberNote,
    isLoading,
    totalPages
  } = useMemberNotes({ isResetCurrentPage });

  const handleCloseConfirmationModal = useCallback(() => {
    setSelectedNoteId(undefined);
  }, []);

  const handleShowConfirmationModal = useCallback((id: string) => {
    setSelectedNoteId(id);
  }, []);

  const handleDeleteNote = useCallback(async () => {
    if (!selectedNoteId) {
      return;
    }

    onResetCurrentPage(true);
    await onDeleteMemberNote(selectedNoteId);
    setSelectedNoteId(undefined);
    onResetCurrentPage(false);
  }, [onDeleteMemberNote, onResetCurrentPage, selectedNoteId]);

  if (isLoading && currentPage === 1) {
    return <Loading loadingMessage="Loading Notes" />;
  }

  return (
    <section className="flex flex-col gap-4 px-6" data-testid="member-notes">
      <MemberNotesTable
        currentPage={currentPage}
        notes={notes}
        onCurrentPageChange={onCurrentPageChange}
        onDeleteNote={handleShowConfirmationModal}
        totalPages={totalPages}
      />
      <ConfirmationModal
        isOpen={!!selectedNoteId}
        confirmationCopy="Delete note"
        onCancelModal={handleCloseConfirmationModal}
        handleConfirm={handleDeleteNote}
        label="Confirm Delete Note"
      >
        Are you sure you want to delete this note?
      </ConfirmationModal>
    </section>
  );
};
